<template>
  <div class="epic-widget-launcher-fields">
    <div v-if="loading" class="epic-widget-launcher-fields__loading">
      <sp-progress size="x-large"></sp-progress>
    </div>

    <div v-if="hasSelects" class="epic-widget-launcher-fields__selects">
      <sp-select
        v-for="field in selects"
        :key="field.name"
        v-preserve-properties:items="field.items"
        v-preserve-properties:min-query-length="field.minQueryLength"
        :name="field.name"
        :label="field.label"
        :value.attr="field.defaultValue"
        required
        :searchable="field.searchable"
        max-results="100"
        :min-query-text="field.minQueryText"
        :no-data-text="field.noDataText"
        :disabled="field.isEmpty"
        :data-testid="field.testId"
      ></sp-select>
    </div>

    <!-- Present before any problem lands, so screen readers announce what is rendered into it. -->
    <div class="epic-widget-launcher-fields__status" role="status">
      <sp-alert v-if="problem" class="epic-widget-launcher-fields__problem" type="error" icon>{{ problem }}</sp-alert>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { vPreserveProperties } from "#ce/utils/property-bindings";
import { t } from "#ce/apps/SpAdmin/SpEpicWidgetLauncher/i18n";
import { useEpicWidgetLauncherStore } from "#ce/apps/SpAdmin/SpEpicWidgetLauncher/store/epic-widget-launcher";

// A list longer than this gets a search bar.
const SEARCHABLE_FROM = 10;
// A list longer than this lists rows only once the search holds `SEARCH_MIN_LENGTH` characters. Browsing renders every
// row, while `max-results` caps only the rows a search matches.
const BROWSABLE_UP_TO = 100;
const SEARCH_MIN_LENGTH = 2;

const { loading, failed, fields, emptyField } = useEpicWidgetLauncherStore();

function toSelect(field) {
  const browsable = field.items.length <= BROWSABLE_UP_TO;

  return {
    ...field,
    label: t(field.label),
    searchable: field.items.length > SEARCHABLE_FROM,
    minQueryLength: browsable ? 0 : SEARCH_MIN_LENGTH,
    minQueryText: browsable ? undefined : t("search_hint", { count: SEARCH_MIN_LENGTH }),
    noDataText: browsable ? undefined : t("no_matches"),
  };
}

const selects = computed(() => fields.value.map(toSelect));
const hasSelects = computed(() => selects.value.length > 0);

const problem = computed(() => {
  if (failed.value) {
    return t("load_error");
  }

  return emptyField.value ? t(emptyField.value.empty) : "";
});
</script>

<style scoped lang="scss">
.epic-widget-launcher-fields {
  &__loading {
    display: grid;
    place-items: center;
    padding-block: var(--sp-ref-scale-40);
  }

  &__selects {
    display: grid;
    gap: var(--sp-ref-scale-24);
  }
}

.epic-widget-launcher-fields__selects + .epic-widget-launcher-fields__status .epic-widget-launcher-fields__problem {
  margin-block-start: var(--sp-ref-scale-24);
}
</style>
